/**
 * PaymentState.js
 *
 * Manages state transformations and data preparation for payment modal.
 * Handles order item grouping, price calculations, and currency formatting.
 */

export class PaymentState {
  constructor(i18n = null) {
    this.i18n = i18n
  }

  /**
   * Prepare display items from order, optionally grouping by item+modifiers
   */
  prepareDisplayItems(order = {}, beforeOrder = false) {
    if (beforeOrder) {
      return Array.isArray(order.items) ? order.items : []
    }

    const sourceItems = Array.isArray(order.items) ? order.items : []
    const grouped = new Map()

    sourceItems.forEach(item => {
      const key = this.buildGroupingKey(item)
      const quantity = this.getItemQuantity(item)
      const unitPrice = this.resolveItemUnitPrice(item)
      const existing = grouped.get(key)

      if (existing) {
        existing.quantity += quantity
        existing.totalPrice += unitPrice * quantity
      } else {
        grouped.set(key, {
          id: item.id,
          name: item.name,
          quantity,
          totalPrice: unitPrice * quantity,
          unitPrice,
          currency: item.currency || order.currency || this.i18n?.config?.settings?.currency || 'PLN',
          modifiers: this.extractModifiers(item),
          image: this.getItemImage(item),
          specialInstructions: this.normalizeSpecialInstructions(item.specialInstructions),
          displayOrder: item.displayOrder ?? 0
        })
      }
    })

    return Array.from(grouped.values()).sort((a, b) => {
      if ((a.displayOrder ?? 0) === (b.displayOrder ?? 0)) {
        return a.name.localeCompare(b.name)
      }
      return (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
    })
  }

  /**
   * Build a unique grouping key for items
   */
  buildGroupingKey(item = {}) {
    const modifiersKey = this.serializeModifiers(item.modifiers || item.selectedModifiers || [])
    const instructions = this.normalizeSpecialInstructions(item.specialInstructions)
    return `${item.id || item.menuItemId || 'unknown'}|${modifiersKey}|${instructions}`
  }

  /**
   * Get item quantity with fallback logic
   */
  getItemQuantity(item = {}) {
    if (typeof item.quantity === 'number' && item.quantity > 0) {
      return item.quantity
    }
    if (typeof item.quantity === 'string') {
      const parsed = parseInt(item.quantity.replace(/[^0-9]/g, ''), 10)
      if (!Number.isNaN(parsed) && parsed > 0) {
        return parsed
      }
    }
    return 1
  }

  /**
   * Resolve item unit price from various possible sources
   */
  resolveItemUnitPrice(item = {}) {
    if (typeof item.unitPrice === 'number') {
      return item.unitPrice
    }
    if (item.unitPrice && typeof item.unitPrice.amount === 'number') {
      return item.unitPrice.amount
    }
    if (typeof item.price === 'number') {
      return item.price
    }
    if (item.price && typeof item.price.amount === 'number') {
      return item.price.amount
    }
    const parsed = parseFloat(item.price)
    if (!Number.isNaN(parsed)) {
      return parsed
    }
    return 0
  }

  /**
   * Extract modifiers from item in normalized format
   */
  extractModifiers(item = {}) {
    const modifiers = item.modifiers || item.selectedModifiers || []
    if (Array.isArray(modifiers)) {
      return modifiers.map(mod => ({ name: mod?.name || mod?.label || mod?.option || mod?.value || mod?.id || '' }))
    }
    if (typeof modifiers === 'object') {
      return Object.values(modifiers).map(value => ({ name: value?.name || value?.label || value || '' }))
    }
    return []
  }

  /**
   * Serialize modifiers for comparison/grouping
   */
  serializeModifiers(modifiers) {
    if (!modifiers) {
      return '[]'
    }
    if (Array.isArray(modifiers)) {
      const normalized = modifiers.map(mod => {
        if (mod && typeof mod === 'object') {
          const group = mod.groupId || mod.group || mod.category || mod.id || mod.name || ''
          const option = mod.optionId || mod.value || mod.id || mod.name || ''
          return { group, option }
        }
        return { value: mod }
      }).sort((a, b) => {
        const aKey = `${a.group || ''}:${a.option || a.value || ''}`
        const bKey = `${b.group || ''}:${b.option || b.value || ''}`
        return aKey.localeCompare(bKey)
      })
      return JSON.stringify(normalized)
    }
    if (typeof modifiers === 'object') {
      const entries = Object.entries(modifiers).map(([group, optionValue]) => {
        if (optionValue && typeof optionValue === 'object') {
          const optionId = optionValue.id || optionValue.value || optionValue.name || optionValue
          return { group, option: optionId }
        }
        return { group, option: optionValue }
      }).sort((a, b) => {
        const aKey = `${a.group || ''}:${a.option || ''}`
        const bKey = `${b.group || ''}:${b.option || ''}`
        return aKey.localeCompare(bKey)
      })
      return JSON.stringify(entries)
    }
    return JSON.stringify(modifiers)
  }

  /**
   * Normalize special instructions
   */
  normalizeSpecialInstructions(value) {
    return (value || '').trim()
  }

  /**
   * Get item image URL
   */
  getItemImage(item) {
    const imageUrl = item.image || item.imageUrl || item.thumbnail;

    console.log('[PaymentState] Item image data:', {
      name: item.name,
      image: item.image,
      imageUrl: item.imageUrl,
      thumbnail: item.thumbnail,
      finalUrl: imageUrl
    });

    return imageUrl || null
  }

  /**
   * Get total price for an item (unit price × quantity)
   */
  getItemTotalPrice(item) {
    if (item.totalPrice != null && !Number.isNaN(Number(item.totalPrice))) {
      return Number(item.totalPrice)
    }

    const price = this.resolveItemUnitPrice(item)
    const quantity = this.getItemQuantity(item)
    return price * quantity
  }

  /**
   * Calculate order total gross amount
   */
  getOrderTotalGross(currentOrder, displayItems, beforeOrder) {
    try {
      if (!beforeOrder && Array.isArray(displayItems) && displayItems.length > 0) {
        return displayItems.reduce((sum, it) => sum + (this.getItemTotalPrice(it) || 0), 0)
      }
      if (currentOrder && currentOrder.total != null) {
        const backendTotal = Number(currentOrder.total)
        if (!Number.isNaN(backendTotal)) {
          return backendTotal
        }
      }
      const items = currentOrder?.items || []
      if (!Array.isArray(items) || items.length === 0) {
        return parseFloat(currentOrder?.total) || 0
      }
      return items.reduce((sum, it) => sum + (this.getItemTotalPrice(it) || 0), 0)
    } catch {
      return parseFloat(currentOrder?.total) || 0
    }
  }

  /**
   * Format currency amount
   */
  formatCurrency(amount, currency) {
    const validAmount = parseFloat(amount) || 0
    return `${validAmount.toFixed(2)} ${currency || 'PLN'}`
  }

  /**
   * Get item modifiers display text
   */
  getItemModifiers(item, t) {
    if (item.modifiers && item.modifiers.length > 0) {
      return item.modifiers.map(mod => mod.name).join(', ')
    }
    return t('cart.regular_portion', 'Regular portion')
  }
}
